import { StartWithUnit } from '@core/state/operators/start-with-unit';
import { inject, Injectable } from '@angular/core';
import { RxActionFactory, RxActions } from '@rx-angular/state/actions';
import { Observable } from 'rxjs';
import { DesignElementsService } from '@api/services/design-elements.service';
import { ApVersionListResponse } from '@api/models/BannerApi/Contract/ap-version-list-response';
import { startWithLoading } from '@core/state/operators/start-with-loading';
import { tap } from 'rxjs/operators';
import { ApVersionSetRequest } from '@api/models/BannerApi/Contract/ap-version-set-request';
import { ApVersionAddRequest } from '@api/models/BannerApi/Contract/ap-version-add-request';
import { ApVersionAddResponse } from '@api/models/BannerApi/Contract/ap-version-add-response';
import { ApElementListResponse } from '@api/models/BannerApi/Contract/ap-element-list-response';
import { ApElementAddResponse } from '@api/models/BannerApi/Contract/ap-element-add-response';
import { DesignElementBody } from '../models/design-element.model-body';
import { ApElementActSetRequest } from '@api/models/BannerApi/Contract/ap-element-act-set-request';
import { ApElementSetResponse } from '@api/models/BannerApi/Contract/ap-element-set-response';
import { CustomRxState } from '@core/state/rx.state';


interface IAppearanceState {
    loading: Record<StartWithUnit, boolean>;
    designElementsVersions: ApVersionListResponse[];
    designElements: ApElementListResponse[];
}

interface AppearanceActions {
    requestDesignElementsVersions: ApVersionListResponse[];
    requestDesignElements: ApElementListResponse[];
}

@Injectable()
export class AppearanceState extends CustomRxState<IAppearanceState> {
    readonly #designElementsService: DesignElementsService = inject(DesignElementsService);
    readonly #actions: RxActions<AppearanceActions> = new RxActionFactory<AppearanceActions>().create();

    public readonly loading$: Observable<Record<StartWithUnit, boolean>> = this.select('loading');
    public readonly designElementsVersions$: Observable<ApVersionListResponse[]> = this.select('designElementsVersions');
    public readonly designElements$: Observable<ApElementListResponse[]> = this.select('designElements');

    constructor() {
        super();

        this.setDefaultState();
        this.connectSelectors();

        this.requestDesignElementsVersions(0);
    }

    public requestDesignElementsVersions(arch: 0 | 1): void {
        this.hold(
            this.#designElementsService.apiBannerApVersionListGet({ arch }),
            this.#actions.requestDesignElementsVersions,
        );
    }

    public requestDesignElements(apvId: number): void {
        this.hold(
            this.#designElementsService.apiBannerApElementListGet({ apvId }).pipe(
                startWithLoading(this, StartWithUnit.RequestDesignElementList),
            ),
            this.#actions.requestDesignElements,
        );
    }

    public addDesignElementsVersion(body: ApVersionAddRequest): Observable<ApVersionAddResponse> {
        return this.#designElementsService.apiBannerApVersionAddPost({ body }).pipe(
            startWithLoading(this, StartWithUnit.AddOrEditDesignElementsVersion),
            tap(() => this.requestDesignElementsVersions(0)),
        );
    }

    public editDesignElementsVersion(body: ApVersionSetRequest): Observable<void> {
        return this.#designElementsService.apiBannerApVersionSetPost({ body }).pipe(
            startWithLoading(this, StartWithUnit.AddOrEditDesignElementsVersion),
            tap(() => this.requestDesignElementsVersions(0)),
        );
    }

    public deleteDesignElementsVersion(apvId: number): Observable<void> {
        return this.#designElementsService.apiBannerApVersionDeleteDelete({ apvId }).pipe(
            startWithLoading(this, StartWithUnit.RemoveDesignElementsVersion),
            tap(() => this.requestDesignElementsVersions(0)),
        );
    }

    public addDesignElement(body: DesignElementBody): Observable<ApElementAddResponse> {
        return this.#designElementsService.apiBannerApElementAddPost({ body }).pipe(
            startWithLoading(this, StartWithUnit.AddOrEditDesignElement),
            tap(() => setTimeout(() => this.requestDesignElements(body.ApvId))),
        );
    }

    public editDesignElement(body: DesignElementBody): Observable<ApElementSetResponse> {
        return this.#designElementsService.apiBannerApElementSetPost({ body }).pipe(
            startWithLoading(this, StartWithUnit.AddOrEditDesignElement),
            tap(() => setTimeout(() => this.requestDesignElements(body.ApvId))),
        );
    }


    public deleteDesignElement(apvId: number, edId: number): Observable<void> {
        return this.#designElementsService.apiBannerApElementDeleteDelete({ apvId, edId }).pipe(
            startWithLoading(this, StartWithUnit.RemoveDesignElement),
            tap(() => setTimeout(() => this.requestDesignElements(apvId))),
        );
    }

    public toggleActiveFlagElement(body: ApElementActSetRequest): Observable<void> {
        return this.#designElementsService.apiBannerApElementActSetPost({ body }).pipe(
            startWithLoading(this, StartWithUnit.AddOrEditDesignElement),
            tap(() => setTimeout(() => this.requestDesignElements(body.apvId))),
        );
    }


    private setDefaultState(): void {
        this.set({
            loading: null,
            designElementsVersions: null,
            designElements: null,
        });
    }

    private connectSelectors(): void {
        this.connect('designElementsVersions', this.#actions.requestDesignElementsVersions$);
        this.connect('designElements', this.#actions.requestDesignElements$);
    }
}
